import React, { useState, useEffect } from 'react';
import { Navbar, Container, Row, Col, Form, Button, Card, Alert, Spinner, Table, ListGroup, Badge, NavDropdown, Nav, Accordion, Tabs, Tab, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import './App.css';
import Visualization from './Visualization';

interface UserSession {
  username: string;
  name: string;
  email: string;
  groups: string[];
}

const EXAMPLES = [
  {
    name: 'Einfach (Transitiv)',
    attributes: 'A, B, C',
    dependencies: 'A -> B\nB -> C'
  },
  {
    name: 'BCNF Verletzung',
    attributes: 'S, T, U',
    dependencies: 'ST -> U\nU -> T'
  },
  {
    name: 'Klausur WS 2024',
    attributes: 'A, B, C, D, E',
    dependencies: 'A -> BC\nAB -> C\nCD -> AB'
  },
  {
    name: 'Komplexes Beispiel',
    attributes: 'A, B, C, D, E, F',
    dependencies: 'A -> BC\nB -> E\nCD -> EF'
  },
  {
    name: "BCNF WS2025/26",
    attributes: 'A, B, C, D, E, F',
    dependencies: 'F->ED\nB->AF'
  }
];

function App() {
  const [user, setUser] = useState<UserSession | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [attributes, setAttributes] = useState(localStorage.getItem('rdt_attributes') || '');
  const [dependencies, setDependencies] = useState(localStorage.getItem('rdt_dependencies') || '');
  const [result, setResult] = useState<any>(null);
  const [currentOp, setCurrentOp] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('result');
  const [showSessionExpired, setShowSessionExpired] = useState(false);

  // Check auth status on mount
  useEffect(() => {
    fetch('/api/me')
      .then(res => {
        if (res.ok) return res.json();
        return null;
      })
      .then(data => {
        if (data) setUser(data);
      })
      .catch(() => {})
      .finally(() => setAuthLoading(false));
  }, []);

  useEffect(() => {
    localStorage.setItem('rdt_attributes', attributes);
  }, [attributes]);

  useEffect(() => {
    localStorage.setItem('rdt_dependencies', dependencies);
  }, [dependencies]);

  const loadExample = (idx: number) => {
    const ex = EXAMPLES[idx];
    setAttributes(ex.attributes);
    setDependencies(ex.dependencies);
    setResult(null);
    setError(null);
  };

  const formatMath = (text: string) => {
    if (!text) return '';
    return text.replace(/->/g, ' → ');
  };

  const renderReductionList = (reductions: any[], phaseType: 'Left' | 'Right') => {
      if (!reductions || reductions.length === 0) return null;
      return (
          <ul className="mb-0 ps-3 small">
              {reductions.map((red: any, k: number) => {
                  const acStep = red.sub_steps?.find((s:any) => s.type === 'AttributeClosure');
                  const successStep = red.sub_steps?.find((s:any) => s.type === 'Success');
                  const failureStep = red.sub_steps?.find((s:any) => s.type === 'Failure');
                  const isSuccess = !!successStep;

                  let closureSteps = null;
                  let finalResult = null;

                  if (acStep) {
                      const steps = acStep.sub_steps?.filter((s:any) => s.type === 'ACStep') || [];
                      finalResult = steps.length > 0 ? steps[steps.length - 1].data.current_set : acStep.data.attributes;

                      closureSteps = (
                          <div className="font-monospace small mt-1">
                              <span className="text-muted">Start: </span>{formatMath(acStep.data.attributes)}
                              {steps.map((step: any, sIdx: number) => (
                                  <span key={sIdx}>
                                      <span className="text-muted mx-1">⎯⎯({formatMath(step.data.dependency)})→</span>
                                      {formatMath(step.data.current_set)}
                                  </span>
                              ))}
                          </div>
                      );
                  }

                  return (
                      <li key={k} className={`mb-3 ${!isSuccess ? 'opacity-75' : ''}`}>
                          <div>Prüfe <span className="font-monospace bg-light px-1">{formatMath(red.data.dependency)}</span> auf <strong className={isSuccess ? "text-danger" : "text-muted"}>{red.data.attribute}</strong>:</div>

                          {acStep ? (
                              <div className="text-muted ms-3 border-start ps-2 my-1">
                                  <div className="fst-italic">Berechne Hülle:</div>
                                  {closureSteps}
                                  <div className="mt-1">Ergebnis: <span className="font-monospace fw-bold">{formatMath(finalResult || "∅")}</span></div>
                              </div>
                          ) : (
                              failureStep?.data && (
                                  <div className="text-muted ms-3 border-start ps-2 my-1 small fst-italic">
                                      {failureStep.data}
                                  </div>
                              )
                          )}

                          {isSuccess ? (
                              <>
                                  <div className="text-success mb-1">
                                    <span className="me-1">✅</span>
                                    Redundant! {phaseType === 'Left' ? "Neue FD: " : "Attribut wird entfernt."}
                                    {phaseType === 'Left' && <span className="font-monospace fw-bold">{formatMath(successStep?.data?.new_fd || "???")}</span>}
                                  </div>
                                  {successStep?.data?.system && (
                                      <div className="ms-3 bg-light p-2 rounded border small">
                                          <div className="text-muted mb-1 fw-bold" style={{fontSize: '0.85em'}}>Resultierendes System:</div>
                                          <div className="font-monospace text-secondary">
                                              {'{ ' + successStep.data.system.map((s:string) => formatMath(s)).join(', ') + ' }'}
                                          </div>
                                      </div>
                                  )}
                              </>
                          ) : (
                              <div className="text-muted fst-italic">
                                  <span className="me-1">❌</span>
                                  Nicht redundant (Attribut wird benötigt).
                              </div>
                          )}
                      </li>
                  );
              })}
          </ul>
      );
  };

  const handleAttributeChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAttributes(event.target.value);
  };

  const handleDependencyChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDependencies(event.target.value);
  };

  const handleSubmit = async (operation: string) => {
    setLoading(true);
    setError(null);
    setResult(null);
    setCurrentOp(operation);
    setActiveTab('result');

    let endpoint = operation;
    if (operation === 'GenerateBCNF') {
      endpoint = 'BCNF';
    } else if (operation === 'Generate3NF') {
      endpoint = 'TNF';
    }

    const body: any = {
      Relation: {
        R: attributes.replace(/[" "\s,]/g, ''),
        F: dependencies
      }
    };

    if (['BCNF', 'TNF', 'CanonicalCover', 'AttributeClosure', 'KeyCandidates'].includes(endpoint)) {
      body.with_report = showDetails;
    }

    try {
      const response = await fetch(`/ret/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.status === 401) {
        setShowSessionExpired(true);
        throw new Error("Sitzung abgelaufen.");
      }

      if (!response.ok) {
        throw new Error(`Server returned status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API Response:', data);
      setResult(data);
    } catch (err: any) {
      if (err.message !== "Sitzung abgelaufen.") {
          setError(err.message || 'Ein unbekannter Fehler ist aufgetreten.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStyledText = (text: string) => {
    const parts = text.split(/(\*\*.*\?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return <span key={index}>{part}</span>;
    });
  };

  const getExplanation = (line: string): string | null => {
    if (line.includes("verletzt die BCNF")) {
      return "Die Boyce-Codd-Normalform (BCNF) ist verletzt, weil die linke Seite dieser Abhängigkeit (Determinante) kein Superschlüssel der Relation ist. In BCNF muss jede Determinante ein Superschlüssel sein.";
    }
    if (line.includes("Linksreduktion")) {
      return "Bei der Linksreduktion prüfen wir, ob Attribute auf der linken Seite (Determinante) überflüssig sind. Ein Attribut A in X->Y ist überflüssig, wenn Y bereits durch (X ohne A) bestimmt wird.";
    }
    if (line.includes("Rechtsreduktion")) {
      return "Bei der Rechtsreduktion prüfen wir, ob Attribute auf der rechten Seite überflüssig sind. Ein Attribut B in X->Y ist überflüssig, wenn X->B transitiv auch ohne diese direkte Abhängigkeit gilt.";
    }
    if (line.includes("kein Superschlüssel")) {
      return "Ein Superschlüssel ist eine Menge von Attributen, die alle anderen Attribute der Relation eindeutig bestimmt. Wenn eine Determinante kein Superschlüssel ist, enthält die Relation Redundanzen.";
    }
    if (line.includes("Split:")) {
      return "Die Relation wird zerlegt, um die Verletzung der Normalform zu beheben. Dabei entstehen zwei neue Relationen: Eine mit der verletzenden Abhängigkeit und eine mit dem Rest.";
    }
    if (line.includes("3. Normalform")) {
      return "Eine Relation ist in 3NF, wenn sie in 2NF ist und kein Nicht-Schlüsselattribut transitiv von einem Schlüsselkandidaten abhängt. Alternativ: Für jede FD X->Y gilt: X ist Superschlüssel ODER Y ist Teil eines Schlüsselkandidaten.";
    }
    if (line.includes("Mandatory (M)")) {
      return "Attribute, die in keiner FD auf der rechten Seite stehen, müssen in jedem Schlüsselkandidaten enthalten sein, da sie von keinem anderen Attribut bestimmt werden können.";
    }
    if (line.includes("Streng abhängig (S)") || line.includes("StrictlyDependent")) {
      return "Attribute, die nur auf rechten Seiten und nie auf linken Seiten von FDs vorkommen, können nicht Teil eines minimalen Schlüssels sein.";
    }
    if (line.includes("Optionale Attribute (O)")) {
      return "Diese Attribute kommen sowohl auf linken als auch auf rechten Seiten vor. Sie müssen ggf. mit den Mandatory-Attributen kombiniert werden, um Schlüssel zu bilden.";
    }
    if (line.includes("M ist bereits ein Superschlüssel") || line.includes("Mandatory ist Superschlüssel")) {
      return "Wenn die Menge der Mandatory-Attribute (M) bereits die gesamte Relation bestimmt, ist M der einzige minimale Schlüsselkandidat.";
    }
    if (line.includes("Prüfe M U") || line.includes("TrySubset")) {
      return "Wir erweitern die Menge der Mandatory-Attribute systematisch um Kombinationen der optionalen Attribute und prüfen, ob diese einen Schlüssel bilden.";
    }
    return null;
  };

  const renderKeyCandidateReport = (jsonRoot: any) => {
    if (!jsonRoot || !jsonRoot.sub_steps) return null;

    let container = jsonRoot.sub_steps.find((s: any) => s.type === 'KeyCandidates');
    if (!container) {
        container = jsonRoot;
    }

    if (!container.sub_steps) return null;

    const mandatoryStep = container.sub_steps.find((s: any) => s.type === 'Mandatory');
    const strictlyDependentStep = container.sub_steps.find((s: any) => s.type === 'StrictlyDependent');
    const optionalStep = container.sub_steps.find((s: any) => s.type === 'Optional');
    const checkMandatoryStep = container.sub_steps.find((s: any) => s.type === 'CheckMandatory');

    const iterationSteps = container.sub_steps.filter((s: any) =>
      ['TrySubset', 'FoundKey', 'Result'].includes(s.type)
    );

    const formatSet = (s: string) => formatMath(s) || "∅";

    return (
      <div className="mt-3">
        <h6 className="border-bottom pb-2 mb-3">1. Attribut-Kategorisierung</h6>
        <Row className="g-2 mb-4">
           <Col md={4}>
             <Card className="h-100 border-primary bg-light">
               <Card.Body className="p-2 text-center">
                 <div className="small text-muted mb-1">Mandatory (M)</div>
                 <div className="fw-bold text-primary fs-5">{formatSet(mandatoryStep?.data)}</div>
                 <small className="text-muted d-block mt-1" style={{fontSize: '0.7rem'}}>Müssen im Schlüssel sein</small>
               </Card.Body>
             </Card>
           </Col>
           <Col md={4}>
             <Card className="h-100 border-danger bg-light">
               <Card.Body className="p-2 text-center">
                 <div className="small text-muted mb-1">Streng Abhängig (S)</div>
                 <div className="fw-bold text-danger fs-5">{formatSet(strictlyDependentStep?.data)}</div>
                 <small className="text-muted d-block mt-1" style={{fontSize: '0.7rem'}}>Nie im Schlüssel</small>
               </Card.Body>
             </Card>
           </Col>
           <Col md={4}>
             <Card className="h-100 border-secondary bg-light">
               <Card.Body className="p-2 text-center">
                 <div className="small text-muted mb-1">Optional (O)</div>
                 <div className="fw-bold text-dark fs-5">{formatSet(optionalStep?.data)}</div>
                 <small className="text-muted d-block mt-1" style={{fontSize: '0.7rem'}}>Kombinationen prüfen</small>
               </Card.Body>
             </Card>
           </Col>
        </Row>

        <h6 className="border-bottom pb-2 mb-3">2. Prüfung</h6>

        {checkMandatoryStep && (
           <Alert variant={checkMandatoryStep.data === true ? "success" : "warning"} className="py-2 mb-3">
             <div className="d-flex align-items-center">
               <span className="me-2">{checkMandatoryStep.data === true ? "✅" : "⚠️"}</span>
               <div>
                 <strong>Prüfe Mandatory-Menge:</strong> {checkMandatoryStep.message}
               </div>
             </div>
           </Alert>
        )}

        {iterationSteps.length > 0 && (
          <ListGroup variant="flush" className="border rounded">
            {iterationSteps.map((step: any, idx: number) => {
              if (step.type === 'Result') return null;

              const isFound = step.type === 'FoundKey';
              return (
                <ListGroup.Item key={idx} variant={isFound ? 'success' : 'light'} className="py-2">
                   <div className="d-flex align-items-center">
                      <span className="me-3" style={{width: '20px'}}>{isFound ? "🔑" : "🔍"}</span>
                      <div>
                        {step.type === 'TrySubset' && (
                          <span>
                            Prüfe Erweiterung um <strong>{formatMath(step.data)}</strong>
                          </span>
                        )}
                         {step.type === 'FoundKey' && (
                          <span>
                            <strong>Schlüssel gefunden:</strong> {formatMath(step.data)}
                          </span>
                        )}
                      </div>
                   </div>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        )}
      </div>
    );
  };

  const renderBCNFReport = (jsonRoot: any) => {
      let root = jsonRoot;
      if (root.type !== 'BCNFDecomposition' && root.sub_steps) {
          root = root.sub_steps.find((s: any) => s.type === 'BCNFDecomposition');
      }
      if (!root || !root.sub_steps) return null;

      const steps = root.sub_steps;

      return (
          <div className="mt-3">
              <h6 className="border-bottom pb-2 mb-3">Zerlegungs-Historie</h6>
              <div className="position-relative">
                  <div className="position-absolute h-100 border-start border-2 ms-3" style={{zIndex:0, top:0, left:0, borderColor: '#dee2e6'}}></div>

                  {steps.map((step: any, idx: number) => {
                      if (step.type === 'Split') {
                          const resultingRelStep = step.sub_steps?.find((s:any) => s.type === 'ResultingRelations');
                          return (
                              <div key={idx} className="d-flex mb-4 position-relative" style={{zIndex:1}}>
                                  <div className="flex-shrink-0 me-3">
                                      <div className="rounded-circle bg-warning d-flex align-items-center justify-content-center border border-white shadow-sm" style={{width:32, height:32}}>
                                          <span style={{fontSize:'1.2em'}}>⚡</span>
                                      </div>
                                  </div>
                                  <div className="flex-grow-1">
                                      <Card className="border-warning shadow-sm">
                                          <Card.Header className="bg-warning bg-opacity-10 text-dark py-2">
                                              <div className="fw-bold small">BCNF Verletzung & Split</div>
                                          </Card.Header>
                                          <Card.Body className="p-3">
                                              <div className="mb-3">
                                                  <div className="small text-muted mb-1">Verletzende Abhängigkeit:</div>
                                                  <code className="bg-light px-2 py-1 rounded text-dark border d-inline-block">
                                                      {formatMath(step.data?.violating_fd)}
                                                  </code>
                                                  <div className="small text-muted mt-1">
                                                      in Relation {formatMath(step.data?.relation)}
                                                  </div>
                                              </div>

                                              {resultingRelStep && (
                                                  <div className="border-top pt-2 mt-2">
                                                      <div className="small text-muted mb-2">Erzeugte Relationen:</div>
                                                      <ul className="mb-0 ps-3">
                                                          {resultingRelStep.data.map((resRel: string, k: number) => (
                                                              <li key={k} className="font-monospace small mb-1">{formatMath(resRel)}</li>
                                                          ))}
                                                      </ul>
                                                  </div>
                                              )}
                                          </Card.Body>
                                      </Card>
                                  </div>
                              </div>
                          );
                      }

                      if (step.type === 'FinalResult') {
                          return (
                              <div key={idx} className="d-flex mb-4 position-relative" style={{zIndex:1}}>
                                  <div className="flex-shrink-0 me-3">
                                      <div className="rounded-circle bg-success d-flex align-items-center justify-content-center border border-white shadow-sm" style={{width:32, height:32}}>
                                          <span className="text-white fw-bold">✓</span>
                                      </div>
                                  </div>
                                  <div className="flex-grow-1">
                                      <Card className="border-success shadow-sm">
                                          <Card.Header className="bg-success text-white py-2">
                                              <div className="fw-bold small">Endergebnis (in BCNF)</div>
                                          </Card.Header>
                                          <Card.Body className="p-3">
                                              <ul className="mb-0 ps-3">
                                                  {step.data.map((resRel: string, k: number) => (
                                                      <li key={k} className="font-monospace mb-2 fw-bold text-success">{formatMath(resRel)}</li>
                                                  ))}
                                              </ul>
                                          </Card.Body>
                                      </Card>
                                  </div>
                              </div>
                          );
                      }

                      return null;
                  })}
              </div>
          </div>
      );
  };

  const render3NFReport = (jsonRoot: any) => {
      let root = jsonRoot;
      if (root.type !== '3NFSynthesis' && root.sub_steps) {
          root = root.sub_steps.find((s: any) => s.type === '3NFSynthesis');
      }
      if (!root || !root.sub_steps) return null;

      const steps = root.sub_steps;

      return (
          <div className="mt-3">
              <h6 className="border-bottom pb-2 mb-3">Synthese-Schritte</h6>
              <div className="position-relative pb-3">
                  <div className="mb-4">
                      <div className="d-flex align-items-center mb-2">
                          <Badge bg="primary" className="me-2 rounded-circle" style={{width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center'}}>1</Badge>
                          <span className="fw-bold">Basis-Relationen (aus kanonischer Überdeckung)</span>
                      </div>
                      {steps.filter((s:any) => s.type === 'InitialRelations').map((s:any, i:number) => (
                          <Card key={i} className="bg-light border-0 ms-4">
                              <Card.Body className="p-2 font-monospace small">
                                  {s.data.map((r:string, k:number) => <div key={k}>{formatMath(r)}</div>)}
                              </Card.Body>
                          </Card>
                      ))}
                  </div>

                  <div className="mb-4">
                      <div className="d-flex align-items-center mb-2">
                          <Badge bg="secondary" className="me-2 rounded-circle" style={{width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center'}}>2</Badge>
                          <span className="fw-bold">Verschmelzung</span>
                      </div>
                      <div className="ms-4">
                        {steps.filter((s:any) => s.type === 'Merge').length === 0 ? (
                            <span className="text-muted small fst-italic">Keine Relationen zusammengefasst.</span>
                        ) : (
                            steps.filter((s:any) => s.type === 'Merge').map((s:any, i:number) => (
                                <div key={i} className="mb-1 small">
                                    <span className="text-success me-2">⚯</span>
                                    Fasse zusammen: <span className="font-monospace text-muted">{formatMath(s.data[0])}</span> + <span className="font-monospace text-muted">{formatMath(s.data[1])}</span>
                                </div>
                            ))
                        )}
                      </div>
                  </div>

                  <div className="mb-4">
                      <div className="d-flex align-items-center mb-2">
                          <Badge bg="info" className="me-2 rounded-circle" style={{width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center'}}>3</Badge>
                          <span className="fw-bold">Schlüssel-Prüfung</span>
                      </div>
                      <div className="ms-4">
                        {steps.filter((s:any) => s.type === 'AddKey').length === 0 ? (
                            <span className="text-success small">✅ Mindestens eine Relation enthält bereits einen Schlüsselkandidaten.</span>
                        ) : (
                            steps.filter((s:any) => s.type === 'AddKey').map((s:any, i:number) => (
                                <Alert key={i} variant="warning" className="p-2 small">
                                    <div>⚠️ Kein Schlüsselkandidat enthalten.</div>
                                    <div className="mt-1">Ergänze Schlüsselrelation: <strong className="font-monospace">{formatMath(s.data)}</strong></div>
                                </Alert>
                            ))
                        )}
                      </div>
                  </div>

                   <div className="mb-2">
                      <div className="d-flex align-items-center mb-2">
                          <Badge bg="success" className="me-2 rounded-circle" style={{width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center'}}>✓</Badge>
                          <span className="fw-bold">Ergebnis (3NF)</span>
                      </div>
                      {steps.filter((s:any) => s.type === 'FinalResult').map((s:any, i:number) => (
                          <Card key={i} className="border-success ms-4">
                              <Card.Body className="p-2 font-monospace">
                                  {s.data.map((r:string, k:number) => (
                                      <div key={k} className="mb-1 fw-bold text-success">{formatMath(r)}</div>
                                  ))}
                              </Card.Body>
                          </Card>
                      ))}
                  </div>
              </div>
          </div>
      );
  };

  const renderCanonicalCoverReport = (jsonRoot: any) => {
      let root = jsonRoot;
      if (root.type !== 'CanonicalCover' && root.sub_steps) {
          root = root.sub_steps.find((s: any) => s.type === 'CanonicalCover');
      }
      if (!root || !root.sub_steps) return null;

      const phases = root.sub_steps.filter((s:any) => s.type === 'Phase');

      return (
          <div className="mt-3">
              <h6 className="border-bottom pb-2 mb-3">Schritte der Kanonischen Überdeckung</h6>
              {phases.map((phase: any, idx: number) => {
                  let content = null;

                  if (phase.message.includes("Linksreduktion")) {
                      const reductions = phase.sub_steps?.filter((s:any) => s.type === 'LeftReduction') || [];
                      content = renderReductionList(reductions, 'Left') || <div className="text-muted small fst-italic">Keine Attribute links reduzierbar.</div>;
                  } else if (phase.message.includes("Rechtsreduktion")) {
                      const reductions = phase.sub_steps?.filter((s:any) => s.type === 'RightReduction') || [];
                      content = renderReductionList(reductions, 'Right') || <div className="text-muted small fst-italic">Keine Attribute rechts reduzierbar.</div>;
                  } else if (phase.message.includes("Vereinfachung")) {
                      const removeStep = phase.sub_steps?.find((s:any) => s.type === 'RemoveRedundant');
                      const removed = removeStep?.sub_steps?.filter((s:any) => s.type === 'Removed') || [];

                      const joinStep = phase.sub_steps?.find((s:any) => s.type === 'JoinSameLHS');
                      const joined = joinStep?.sub_steps?.filter((s:any) => s.type === 'Joined') || [];

                      const resultStep = phase.sub_steps?.find((s:any) => s.type === 'SimplificationResult');

                      if (removed.length === 0 && joined.length === 0) {
                          content = (
                              <div>
                                  <div className="text-muted small fst-italic mb-2">Keine Vereinfachungen notwendig.</div>
                                  {resultStep?.data?.system && (
                                      <div className="bg-light p-2 rounded border small">
                                          <div className="text-muted mb-1 fw-bold" style={{fontSize: '0.85em'}}>Resultierendes System:</div>
                                          <div className="font-monospace text-secondary">
                                              {'{ ' + resultStep.data.system.map((s:string) => formatMath(s)).join(', ') + ' }'}
                                          </div>
                                      </div>
                                  )}
                              </div>
                          );
                      } else {
                          content = (
                              <div>
                                  <ul className="mb-3 ps-3 small">
                                      {removed.map((s:any, k:number) => (
                                          <li key={'rem'+k} className="mb-1 text-danger">
                                              <span className="me-2">🗑️</span>
                                              Entferne Trivial/Leer: <span className="font-monospace text-decoration-line-through">{formatMath(s.data)}</span>
                                          </li>
                                      ))}
                                      {joined.map((s:any, k:number) => (
                                          <li key={'join'+k} className="mb-1 text-primary">
                                              <span className="me-2">🔗</span>
                                              Fasse zusammen: <span className="font-monospace">{formatMath(s.data[0])}</span> + <span className="font-monospace">{formatMath(s.data[1])}</span>
                                          </li>
                                      ))}
                                  </ul>
                                  {resultStep?.data?.system && (
                                      <div className="bg-light p-2 rounded border small">
                                          <div className="text-muted mb-1 fw-bold" style={{fontSize: '0.85em'}}>Resultierendes System:</div>
                                          <div className="font-monospace text-secondary">
                                              {'{ ' + resultStep.data.system.map((s:string) => formatMath(s)).join(', ') + ' }'}
                                          </div>
                                      </div>
                                  )}
                              </div>
                          );
                      }
                  }

                  return (
                      <Card key={idx} className="mb-3 border-light shadow-sm">
                          <Card.Header className="bg-white border-bottom fw-bold text-primary">
                              {phase.message}
                          </Card.Header>
                          <Card.Body>
                              {content}
                          </Card.Body>
                      </Card>
                  );
              })}
          </div>
      );
  };

  const renderReport = () => {
    if (result.report_json) {
        if (currentOp === 'KeyCandidates') {
            return (
                <Accordion className="mt-3">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>Detaillierter Rechenweg</Accordion.Header>
                        <Accordion.Body>{renderKeyCandidateReport(result.report_json)}</Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            );
        }
        if (currentOp === 'CanonicalCover') {
            return (
                 <Accordion className="mt-3">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>Schritte der Kanonischen Überdeckung</Accordion.Header>
                        <Accordion.Body>{renderCanonicalCoverReport(result.report_json)}</Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            );
        }
        if (currentOp === 'GenerateBCNF') {
            return (
                 <Accordion className="mt-3">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>Zerlegungsbaum (BCNF)</Accordion.Header>
                        <Accordion.Body>{renderBCNFReport(result.report_json)}</Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            );
        }
        if (currentOp === 'Generate3NF') {
            return (
                 <Accordion className="mt-3">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>Synthese-Schritte (3NF)</Accordion.Header>
                        <Accordion.Body>{render3NFReport(result.report_json)}</Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            );
        }
    }

    const report = result.Report || result.report;
    if (!report || report.length === 0) return null;

    return (
      <Accordion className="mt-3">
        <Accordion.Item eventKey="0">
          <Accordion.Header>Rechenweg / Details</Accordion.Header>
          <Accordion.Body className="bg-light p-2">
            <div className="font-monospace small" style={{ whiteSpace: 'pre-wrap' }}>
              {report.map((line: string, idx: number) => {
                const spaces = line.search(/\S|$|/);
                const indentLevel = Math.floor(spaces / 2);
                const content = line.trim();
                const formattedContent = formatMath(content);
                const explanation = getExplanation(content);

                let className = "mb-1 d-flex align-items-center";
                let textClass = "";

                if (content.startsWith("> **Split:")) textClass += " text-primary fw-bold";
                if (content.includes("❌")) textClass += " text-danger";
                if (content.includes("✅")) textClass += " text-success";
                if (content.startsWith("**Erhalte") || content.startsWith("**Ergebnis")) textClass += " mt-2 border-top pt-1 text-success fw-bold";

                const lineContent = (
                  <div style={{ paddingLeft: `${indentLevel * 1.5}rem` }} className={className}>
                    <span className={textClass + " me-2"}>{renderStyledText(formattedContent)}</span>
                    {explanation && (
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`tooltip-${idx}`}>{explanation}</Tooltip>}
                      >
                        <Badge bg="info" pill style={{ cursor: 'help', fontSize: '0.6em' }}>?</Badge>
                      </OverlayTrigger>
                    )}
                  </div>
                );

                return <div key={idx}>{lineContent}</div>;
              })}
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );
  };

  const renderResult = () => {
    if (!result) return null;

    let content = null;

    if (currentOp === 'KeyCandidates') {
      const candidates = result.key_candidates;
      content = (
        <div>
          <h5>Schlüsselkandidaten</h5>
          {candidates && candidates.length > 0 ? (
            <ListGroup>
              {candidates.map((key: string, idx: number) => (
                <ListGroup.Item key={idx} className="font-monospace">{formatMath(key)}</ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <Alert variant="info">Keine Schlüsselkandidaten gefunden.</Alert>
          )}
        </div>
      );
    } else if (currentOp === 'CheckNormalForms') {
      const { Result } = result;
      if (!Result) return <Alert variant="warning">Ungültige Antwort vom Server.</Alert>;

      const checkRow = (label: string, value: string) => {
        const isTrue = value === 'true';
        return (
          <tr key={label}>
            <td>{label}</td>
            <td>
              {isTrue ? (
                <Badge bg="success">Erfüllt</Badge>
              ) : (
                <>
                  <Badge bg="danger" className="me-2">Nicht erfüllt</Badge>
                  <span className="text-muted small">{formatMath(value.replace('false: ', ''))}</span>
                </>
              )}
            </td>
          </tr>
        );
      };

      content = (
        <div>
          <h5 className="mb-3">Normalformen-Analyse</h5>
          <Table bordered hover responsive>
            <tbody>
              {checkRow('2. Normalform', Result.is_2nf)}
              {checkRow('3. Normalform', Result.is_3nf)}
              {checkRow('Boyce-Codd-Normalform', Result.is_bcnf)}
            </tbody>
          </Table>

          <div className="mt-4">
            <h6>Prime Attribute</h6>
            <div className="p-2 bg-light border rounded font-monospace mb-3">
               {formatMath(Result.prime_attributes) || <span className="text-muted">-</span>}
            </div>

            <h6>Schlüsselkandidaten</h6>
            {Result.key_candidates && Result.key_candidates.length > 0 ? (
               <ListGroup>
                 {Result.key_candidates.map((key: string, idx: number) => (
                   <ListGroup.Item key={idx} className="font-monospace py-1">{formatMath(key)}</ListGroup.Item>
                 ))}
               </ListGroup>
            ) : <span className="text-muted">Keine</span>}
          </div>
        </div>
      );
    } else if (currentOp === 'CanonicalCover') {
        content = (
            <div>
                <h5>Kanonische Überdeckung</h5>
                {result.FCList && result.FCList.length > 0 ? (
                    <ListGroup variant="flush">
                        {result.FCList.map((fd: string, idx: number) => (
                            <ListGroup.Item key={idx} className="font-monospace">
                                {formatMath(fd)}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                ) : (
                    <div className="p-3 bg-light border rounded font-monospace text-wrap text-break">
                        {formatMath(result.FC) || "Keine"}
                    </div>
                )}
            </div>
        );
    } else if (currentOp === 'GenerateBCNF' || currentOp === 'Generate3NF') {
        const relations = currentOp === 'GenerateBCNF' ? result.bcnf_relations : result.tnf_relations;
        const title = currentOp === 'GenerateBCNF' ? "BCNF Zerlegung" : "3NF Synthese";

        content = (
            <div>
                <h5>{title}</h5>
                {relations && relations.length > 0 ? (
                    <ListGroup variant="flush">
                        {relations.map((rel: string, idx: number) => (
                            <ListGroup.Item key={idx} className="font-monospace">
                                {formatMath(rel)}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                ) : (
                    <Alert variant="warning">Keine Zerlegung erhalten.</Alert>
                )}
            </div>
        );
    } else {
        content = (
            <div className="result-container">
                <pre className="m-0">{JSON.stringify(result, null, 2)}</pre>
            </div>
        );
    }

    return (
        <Tabs
          id="result-tabs"
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || 'result')}
          className="mb-3"
        >
          <Tab eventKey="result" title="Bericht">
            <div className="mt-3">
              {content}
              {renderReport()}
            </div>
          </Tab>
          <Tab eventKey="viz" title="Visualisierung">
            <div className="mt-3">
              <Visualization
                attributes={attributes}
                dependencies={dependencies}
                reportJson={result.report_json}
              />
            </div>
          </Tab>
        </Tabs>
    );
  };

  if (authLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  // Landing Page for unauthenticated users
  if (!user) {
    return (
      <div className="App">
        <Navbar bg="dark" variant="dark" expand="lg" className="mb-4 shadow-sm">
          <Container>
            <Navbar.Brand href="#home">Relationale Entwurfstheorie</Navbar.Brand>
            <Nav className="ms-auto">
              <Button variant="outline-light" size="sm" onClick={() => { window.location.href = '/auth/login'; }}>Login</Button>
            </Nav>
          </Container>
        </Navbar>

        <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center mb-5">
            <h1 className="display-4 fw-bold mb-3">Relationale Entwurfstheorie</h1>
            <p className="lead text-muted mb-4" style={{ maxWidth: '600px' }}>
              Ein interaktives Werkzeug zur Analyse, Normalisierung und Visualisierung von relationalen Datenbankschemata.
            </p>
            <div className="d-grid gap-3 d-sm-flex justify-content-sm-center">
              <Button size="lg" onClick={() => { window.location.href = '/auth/login'; }}>
                Jetzt anmelden
              </Button>
            </div>
          </div>

          <Row className="w-100 g-4">
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm text-center p-3">
                <Card.Body>
                  <h3 className="h5 mb-3">🔍 Analyse</h3>
                  <p className="text-muted small">Bestimmung von Schlüsselkandidaten, Attributhüllen und Überprüfung auf Normalformen (2NF, 3NF, BCNF).</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm text-center p-3">
                <Card.Body>
                  <h3 className="h5 mb-3">🛠️ Synthese</h3>
                  <p className="text-muted small">Automatische Zerlegung in BCNF, Synthese in die 3. Normalform und Berechnung kanonischer Überdeckungen.</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm text-center p-3">
                <Card.Body>
                  <h3 className="h5 mb-3">📊 Visualisierung</h3>
                  <p className="text-muted small">Interaktive Graphen zur Darstellung komplexer funktionaler Abhängigkeiten und detaillierte Rechenwege.</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="App">
      <Navbar bg="dark" variant="dark" expand="lg" className="mb-4 shadow-sm">
        <Container>
          <Navbar.Brand href="#home">Relationale Entwurfstheorie</Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto align-items-center">
              <NavDropdown title="Beispiele laden" id="basic-nav-dropdown" className="me-3">
                {EXAMPLES.map((ex, idx) => (
                  <NavDropdown.Item key={idx} onClick={() => loadExample(idx)}>
                    {ex.name}
                  </NavDropdown.Item>
                ))}
              </NavDropdown>

              <div className="d-flex align-items-center">
                <span className="text-light me-3 small">
                  {user.username || user.name || user.email || "Benutzer"}
                </span>
                <Button variant="outline-light" size="sm" onClick={() => { window.location.href = '/auth/logout'; }}>
                  Logout
                </Button>
              </div>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container>
        <Row className="justify-content-center">
          <Col lg={5} className="mb-4">
            <Card className="shadow-sm h-100">
              <Card.Header>Eingabe</Card.Header>
              <Card.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Attribute</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={attributes}
                      onChange={handleAttributeChange}
                      placeholder="z.B. A, B, C, D"
                      disabled={loading}
                    />
                    <Form.Text className="text-muted">
                      Kommaseparierte Liste von Attributen.
                    </Form.Text>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Funktionale Abhängigkeiten</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      value={dependencies}
                      onChange={handleDependencyChange}
                      placeholder="z.B. A->B, B->C"
                      disabled={loading}
                    />
                    <Form.Text className="text-muted">
                      Pro Zeile eine Abhängigkeit (z.B. A-&gt;B).
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Check
                      type="switch"
                      id="details-switch"
                      label="Rechenweg anzeigen (wo verfügbar)"
                      checked={showDetails}
                      onChange={(e) => setShowDetails(e.target.checked)}
                      disabled={loading}
                    />
                  </Form.Group>

                  <div className="section-label mt-2">Analyse</div>
                  <div className="d-grid gap-2 mb-4">
                    <Button variant="outline-primary" onClick={() => handleSubmit('KeyCandidates')} disabled={loading}>
                      Schlüsselkandidaten
                    </Button>
                    <Button variant="outline-primary" onClick={() => handleSubmit('CheckNormalForms')} disabled={loading}>
                      Normalformen prüfen
                    </Button>
                  </div>

                  <div className="section-label">Normalisierung</div>
                  <div className="d-grid gap-2">
                    <Button variant="outline-success" onClick={() => handleSubmit('GenerateBCNF')} disabled={loading}>
                      BCNF-Zerlegung
                    </Button>
                    <Button variant="outline-success" onClick={() => handleSubmit('Generate3NF')} disabled={loading}>
                      3NF-Zerlegung
                    </Button>
                    <Button variant="outline-danger" onClick={() => handleSubmit('CanonicalCover')} disabled={loading}>
                      Kanonische Überdeckung
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={7} className="mb-4">
            <Card className="shadow-sm h-100">
              <Card.Header>Ergebnis</Card.Header>
              <Card.Body className="d-flex flex-column">
                {error && <Alert variant="danger">{error}</Alert>}

                {loading ? (
                  <div className="d-flex justify-content-center align-items-center flex-grow-1" style={{ minHeight: '300px' }}>
                    <Spinner animation="border" role="status" variant="primary">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  </div>
                ) : (
                  result ? (
                    <div className="flex-grow-1">
                      {renderResult()}
                    </div>
                  ) : (
                    <div className="text-muted text-center d-flex justify-content-center align-items-center flex-grow-1" style={{ minHeight: '300px' }}>
                      Starten Sie eine Berechnung, um das Ergebnis hier zu sehen.
                    </div>
                  )
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal show={showSessionExpired} onHide={() => {}} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>Sitzung abgelaufen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an, um fortzufahren.<br/>
          <small className="text-muted">Ihre Eingaben wurden lokal gespeichert und stehen nach der Anmeldung wieder zur Verfügung.</small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => { window.location.href = '/auth/login'; }}>
            Erneut anmelden
          </Button>
        </Modal.Footer>
      </Modal>

      <footer className="text-center py-3 text-muted mt-auto border-top bg-light">
        <Container>
          <small>
            Relational Design Theory Tool &copy; {new Date().getFullYear()} • Version: {process.env.REACT_APP_VERSION || 'dev'}
          </small>
        </Container>
      </footer>
    </div>
  );
}

export default App;
